/**
 * Client-side telemetry for the JWT refresh lifecycle.
 *
 * Four of the ways a refresh can fail never reach the server at all -- the empty-refresh-id short
 * circuit, the Web Lock acquisition timeout, the request timeout, and being offline -- so no amount
 * of server-side instrumentation can see them. This module is what makes them countable (AB#10739).
 *
 * Three deliberate choices:
 *
 * 1. **The SDK loads from Microsoft's CDN, not from npm.** Webpack here has ~30 independent entry
 *    points with no shared vendor chunk, so bundling `@microsoft/applicationinsights-web` would add
 *    a copy to every bundle that imports this file. A CDN script is fetched and cached once per
 *    browser. CityzenWeb declares no `script-src`, `connect-src` or `default-src` anywhere -- every
 *    CSP in the app is `frame-ancestors`-only -- so nothing blocks either the script or its POSTs.
 *
 * 2. **It initialises itself from this module rather than from a Razor partial.** A partial would
 *    have to be added to three `Default-Head*.cshtml` files AND every standalone view that sets
 *    `Layout = null`, because none of those include any head partial. Initialising here means
 *    telemetry exists exactly where refresh code runs, which is the correct set by construction.
 *
 * 3. **It fails silent and never throws.** Telemetry that can break a page is worse than no
 *    telemetry. Every entry point is wrapped, an unconfigured page (localhost with no key) no-ops
 *    permanently after one check, and a failed script load disables the module rather than retrying.
 */

const SDK_URL = "https://js.monitor.azure.com/scripts/b/ai.3.gbl.min.js";

/** Calls made before the SDK finishes loading are queued, not dropped. */
const MAX_QUEUED = 50;

type TelemetryProperties = Record<string, string>;
type TelemetryMetrics = Record<string, number>;

interface TrackedEvent {
  name: string;
  properties?: TelemetryProperties;
  measurements?: TelemetryMetrics;
}

interface AppInsightsLike {
  trackEvent(event: TrackedEvent): void;
}

declare global {
  interface Window {
    Microsoft?: {
      ApplicationInsights?: {
        ApplicationInsights: new (options: {
          config: Record<string, unknown>;
        }) => AppInsightsLike & { loadAppInsights(): void };
      };
    };
  }
}

type State = "idle" | "loading" | "ready" | "disabled";

interface SharedTelemetryState {
  state: State;
  sdk: AppInsightsLike | null;
  queued: TrackedEvent[];
  alwaysSent: number;
}

declare global {
  interface Window {
    __piTelemetry?: SharedTelemetryState;
  }
}

/**
 * Shared across bundles on purpose, exactly like `window.__TokenRefreshService`.
 *
 * Module state would be per-bundle, and this app routinely loads several bundles into one page --
 * Views/Customer/Project.cshtml loads five, all of which import ApiProvider and therefore this
 * module. Per-bundle state would mean five script injections, five App Insights cores and five
 * senders on the busiest admin page in the product. Events would not be double-counted (each
 * trackEvent reaches exactly one copy), so the symptom is waste rather than wrong numbers, which
 * is precisely the kind of thing that never gets noticed.
 */
const shared: SharedTelemetryState = (window.__piTelemetry ??= {
  state: "idle",
  sdk: null,
  queued: [],
  alwaysSent: 0
});

// Indexed off the declared shape rather than an `unknown` cast, so renaming a key in global.d.ts
// is a compile error here instead of a silent undefined at runtime.
function config<K extends keyof Window["_JSCONFIG"]>(
  key: K
): Window["_JSCONFIG"][K] | undefined {
  return window._JSCONFIG?.[key];
}

function connectionString(): string | null {
  const value = config("AppInsightsConnectionString");
  return typeof value === "string" && value.length > 0 ? value : null;
}

/**
 * Fraction of *successful* refreshes to record, 0..1. Failures ignore this entirely -- they are the
 * signal, and a sampled failure count is worse than none because it still looks like a number.
 * Successes are sampled only so a stuck refresh loop cannot turn one browser into a firehose.
 */
function successSampleRate(): number {
  const value = config("ClientTelemetrySampleRate");
  return typeof value === "number" && value >= 0 && value <= 1 ? value : 0.1;
}

/**
 * How many unsampled (failure) events one page may send before they start being sampled too.
 * Config-driven for the same reason the sample rate is: a ceiling that can only be changed by a
 * deploy is not a control.
 */
function alwaysSendCeiling(): number {
  const value = config("ClientTelemetryAlwaysSendCeiling");
  return typeof value === "number" && value > 0 ? value : 50;
}

function flushQueue(): void {
  if (!shared.sdk) return;
  while (shared.queued.length) {
    const event = shared.queued.shift();
    if (!event) break;
    try {
      shared.sdk.trackEvent(event);
    } catch (_) {
      // A single bad event must not strand the rest of the queue.
    }
  }
}

function load(): void {
  const cs = connectionString();
  if (!cs) {
    // Not configured on this page or in this environment. Never ask again.
    shared.state = "disabled";
    shared.queued.length = 0;
    return;
  }

  shared.state = "loading";

  const script = document.createElement("script");
  script.src = SDK_URL;
  script.async = true;

  script.onload = () => {
    try {
      const ctor = window.Microsoft?.ApplicationInsights?.ApplicationInsights;
      if (!ctor) {
        shared.state = "disabled";
        shared.queued.length = 0;
        return;
      }

      const instance = new ctor({
        config: {
          connectionString: cs,
          // The page's own pageviews, ajax calls and exceptions are not ours to collect here. This
          // module exists for the refresh lifecycle; turning on auto-collection would quietly add a
          // second analytics pipeline to every admin page and bury the events we came for.
          disableAjaxTracking: true,
          disableFetchTracking: true,
          autoTrackPageVisitTime: false,
          disableExceptionTracking: true,
          disableTelemetry: false,
          // No ai_user / ai_session cookies. Left on, the SDK sets a long-lived first-party
          // identity cookie on every page it loads -- which on this platform includes anonymous
          // resident-facing engagement pages and iframes embedded on customers' own government
          // sites. That is pseudonymous tracking of residents with no consent gate, and nothing
          // here needs it: correlation uses the attemptId this module generates, not the SDK's
          // user id.
          disableCookiesUsage: true
        }
      });

      instance.loadAppInsights();
      shared.sdk = instance;
      shared.state = "ready";
      flushQueue();
    } catch (_) {
      shared.state = "disabled";
      shared.queued.length = 0;
    }
  };

  script.onerror = () => {
    // Blocked by an extension, offline, or the CDN is down. One attempt, then stop.
    shared.state = "disabled";
    shared.queued.length = 0;
  };

  try {
    document.head.appendChild(script);
  } catch (_) {
    shared.state = "disabled";
    shared.queued.length = 0;
  }
}

/**
 * Records one event. `alwaysSend` marks a failure, which bypasses success sampling.
 *
 * Never throws and never returns a value the caller has to check -- a caller that has to handle a
 * telemetry failure will eventually handle it wrongly on a path that matters.
 */
export function trackEvent(
  name: string,
  properties?: TelemetryProperties,
  measurements?: TelemetryMetrics,
  alwaysSend = false
): void {
  try {
    if (shared.state === "disabled") return;

    // An always-send event bypasses sampling, which is right for a failure and wrong without a
    // ceiling: `request-failed-transient` fires on the path that deliberately does not clear auth
    // and does not redirect, so a polling widget whose refresh keeps failing emits one unsampled
    // event per poll, from every open tab, indefinitely. Past the ceiling these fall back to the
    // sampled path rather than stopping, so the signal survives at a known rate.
    const overCeiling = alwaysSend && shared.alwaysSent >= alwaysSendCeiling();
    const unsampled = alwaysSend && !overCeiling;

    const rate = successSampleRate();
    if (!unsampled && Math.random() >= rate) return;

    if (unsampled) {
      shared.alwaysSent += 1;
    }

    // Stamped centrally so every caller gets it and no caller has to remember. The path alone
    // separates an embed or public dashboard from an admin surface, which is what turns "refreshes
    // fail a lot" into "refreshes fail on this page", and it carries no query string or hash --
    // those are where ids and tokens live, and this is telemetry, not a session recorder.
    const event: TrackedEvent = {
      name,
      properties: {
        ...(properties || {}),
        page: (typeof location !== "undefined" && location.pathname) || "unknown"
      },
      measurements: {
        ...(measurements || {}),
        // The rate this event survived at. Failures are always sent, so they carry 1: a consumer
        // that divides by sampleRate gets the right answer for both kinds without needing to know
        // which is which. Without this on the event, a success count read off these rows is wrong
        // by a factor of 1/rate and there is nothing in the data that says so.
        sampleRate: unsampled ? 1 : rate
      }
    };

    if (shared.state === "ready" && shared.sdk) {
      shared.sdk.trackEvent(event);
      return;
    }

    if (shared.queued.length < MAX_QUEUED) {
      shared.queued.push(event);
    }

    if (shared.state === "idle") {
      load();
    }
  } catch (_) {
    // Deliberately swallowed. See the module note above.
  }
}

// Kicked off at module load rather than on first use. The lazy version lost precisely the event
// most worth having: redirectToLogin() calls trackEvent and then immediately navigates, so on a page
// whose first telemetry event IS the forced logout, that event was still sitting in `queued` while
// the CDN script was in flight, and the navigation discarded it. Warming here costs one cached
// script request on pages that already run refresh code, and by the time a session dies -- minutes
// in -- the SDK is loaded and trackEvent hands straight to its buffer, which flushes on unload.
try {
  if (shared.state === "idle" && connectionString() && sessionCanRefresh()) {
    load();
  }
} catch (_) {
  shared.state = "disabled";
}

/**
 * True when this page holds a session that can actually refresh.
 *
 * The warm-up below is deliberately NOT unconditional. A session with an empty stored refresh id --
 * an embed or the public dashboard -- can never rotate, so loading a telemetry SDK there buys no
 * refresh signal and costs an external script and an ingestion identity on an anonymous page. The
 * empty-string sentinel is the same one TokenRefreshService.rotate treats as terminal.
 */
function sessionCanRefresh(): boolean {
  try {
    return (
      (localStorage.getItem("_internalRefreshTokenId") || "").length > 0 ||
      (document.cookie || "").indexOf("_internalRefreshTokenId=") !== -1
    );
  } catch (_) {
    return false;
  }
}

/**
 * A correlation id for one refresh attempt, sent to the server as `X-Refresh-Attempt-Id` and
 * recorded on both halves so the client event and the server event can be joined.
 */
export function newAttemptId(): string {
  try {
    const cryptoApi = window.crypto as
      | (Crypto & { randomUUID?: () => string })
      | undefined;

    if (cryptoApi?.randomUUID) {
      return cryptoApi.randomUUID();
    }

    if (cryptoApi?.getRandomValues) {
      const bytes = new Uint8Array(16);
      cryptoApi.getRandomValues(bytes);
      return Array.from(bytes)
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("");
    }
  } catch (_) {
    // Fall through to the non-crypto id below.
  }

  // Not a UUID and not unique across the fleet, but it only has to distinguish concurrent attempts
  // in one browser long enough to join two telemetry rows.
  return `${Date.now().toString(16)}-${Math.random().toString(16).slice(2, 10)}`;
}
